import { useState } from "react";
import { CheckCheck, Loader2 } from "lucide-react";
import { FieldRow } from "./FieldRow";
import { WidgetIcons } from "./icons";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { WidgetField, WidgetGroup } from "@/domains/buildings/types/buildingWidget";

interface GroupProps {
  group: WidgetGroup;
  open: boolean;
  onToggle: () => void;
  registryCode?: string | null;
  onDocPreview: (filename: string) => void;
  orgId: string | undefined;
  buildingId: string | undefined;
  /** When false, fields are read-only (no editing/confirming). */
  canManage: boolean;
  onFieldSelect?: (field: WidgetField) => void;
  selectedFieldId?: string | null;
  onConfirmAll?: (fields: WidgetField[]) => Promise<void>;
}

/**
 * One Building Data section. Every field renders as a row — missing ones show
 * no value and are filled in directly; values that need review remain visible
 * and can be confirmed as a section.
 */
export function Group({
  group,
  open,
  onToggle,
  registryCode,
  onDocPreview,
  orgId,
  buildingId,
  canManage,
  onFieldSelect,
  selectedFieldId,
  onConfirmAll,
}: GroupProps) {
  const [confirming, setConfirming] = useState(false);
  const { t } = useLanguage();
  const tw = t.buildings.buildingDataWidget;
  const confirmed = group.fields.filter((field) => field.state === "confirmed").length;
  const pending = group.fields.filter((field) => field.state === "pending");
  const confirmablePending = pending.filter((field) => !field.hasConflict);
  const total = group.fields.length;

  const confirmAll = async () => {
    if (!onConfirmAll || confirmablePending.length === 0 || confirming) return;
    setConfirming(true);
    try {
      await onConfirmAll(confirmablePending);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <section data-bd-section={group.id} className="scroll-mt-4 border-b border-border pb-4">
      <div className="sticky top-0 z-10 flex min-h-12 items-center gap-2 bg-background/95 py-2 backdrop-blur-sm">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 border-0 bg-transparent p-0 text-left"
        >
          <span
            className="inline-flex text-muted-foreground transition-transform"
            style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
          >
            <WidgetIcons.chevRight size={14} />
          </span>
          <span className="truncate text-[17px] font-semibold tracking-[-0.005em] text-foreground">
            {group.title}
          </span>
          {pending.length > 0 ? (
            <span
              aria-label={tw.pendingGroupHint}
              className="rounded-full bg-warning/20 px-2 py-0.5 text-[11px] font-semibold text-warning-900"
            >
              {pending.length}
            </span>
          ) : null}
          <span className="text-[11px] text-muted-foreground">
            {confirmed}/{total}
          </span>
        </button>

        {canManage && confirmablePending.length > 0 ? (
          <button
            type="button"
            onClick={() => void confirmAll()}
            disabled={confirming}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
          >
            {confirming ? <Loader2 size={13} className="animate-spin" /> : <CheckCheck size={13} />}
            {tw.confirmAll.replace("{count}", String(confirmablePending.length))}
          </button>
        ) : null}
      </div>

      <div
        className="overflow-hidden transition-[max-height,opacity] duration-200"
        style={{ maxHeight: open ? 10000 : 0, opacity: open ? 1 : 0 }}
      >
        <div className="space-y-0.5">
          {group.fields.map((field) => (
            <FieldRow
              key={field.id}
              field={field}
              registryCode={registryCode}
              onDocPreview={onDocPreview}
              orgId={orgId}
              buildingId={buildingId}
              canManage={canManage}
              onSelect={onFieldSelect}
              selected={selectedFieldId === field.id}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
